import React, { useMemo, useState } from 'react';
import './FactureDetailModal.css';

// Fiche d'une facture atelier (table factures_services), PARTAGÉE (2026-10-26).
// Remplace six copies de la même modale (Réception VIP, Litiges, Gestion
// Parking, Rapport parking, Atelier, Factures) : le total était la dixième de
// dix cases identiques, et les colonnes Qté / Montant sortaient à droite.
//
// Ordre de lecture : n° + total, parcours de l'OR (ouverture → facture),
// véhicule / client, répartition, lignes groupées par nature.
// Nature d'une ligne : le CSV source (Ateliers.all.csv) met « Pièces » dans
// `prestation` pour une pièce, et le LIBELLÉ DE L'OPÉRATION pour la main
// d'œuvre et les forfaits (« FORFAIT SYSTEMATIQUE ENTRETIEN MAJEUR »…) ;
// « M.O. » / « M.0. » sur les anciens imports. Il n'y a pas de 3e nature.
// La quantité d'une ligne de main d'œuvre n'est PAS un nombre d'heures (le CSV
// ne le donne pas) : elle est affichée telle quelle.

const EST_PIECE = (p) => /^pi.ces$/i.test(String(p || '').trim()) || String(p || '').trim().toUpperCase() === 'PIECES';

const montantFr = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 })
  .format(Number(v) || 0);

// 'AAAA-MM-JJ' ou ISO → Date locale (sans décalage de fuseau)
function jour(v) {
  if (!v) return null;
  const [y, m, d] = String(v).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
const dateFr = (d) => (d ? d.toLocaleDateString('fr-FR') : '—');

function FactureDetailModal({ facture, onClose, onOuvrirOr = null }) {
  const [copie, setCopie] = useState(false);

  const groupes = useMemo(() => {
    const lignes = Array.isArray(facture?.lignes_json) ? facture.lignes_json : [];
    const mo = lignes.filter(l => !EST_PIECE(l.prestation));
    const pieces = lignes.filter(l => EST_PIECE(l.prestation));
    const somme = (ls) => ls.reduce((s, l) => s + (Number(l.montant_ht) || 0), 0);
    return [
      { cle: 'mo', titre: 'Main d’œuvre et forfaits', lignes: mo, total: somme(mo) },
      { cle: 'pieces', titre: 'Pièces', lignes: pieces, total: somme(pieces) },
    ].filter(g => g.lignes.length > 0);
  }, [facture]);

  if (!facture) return null;

  const total = Number(facture.montant_ht) || 0;
  const nbLignes = groupes.reduce((n, g) => n + g.lignes.length, 0);
  const dFacture = jour(facture.date_facture);
  const dOuverture = jour(facture.date_ouverture);
  const duree = dFacture && dOuverture ? Math.round((dFacture - dOuverture) / 86400000) : null;
  const sommeAbs = groupes.reduce((s, g) => s + Math.abs(g.total), 0);
  const entete = [facture.societe, facture.department].filter(Boolean).join(' · ');

  const copierNumero = async () => {
    try {
      await navigator.clipboard.writeText(facture.num_facture || '');
      setCopie(true);
      setTimeout(() => setCopie(false), 1400);
    } catch { /* presse-papier refusé : rien à faire */ }
  };

  return (
    <div className="modal-overlay fd-overlay">
      <div className="modal-content fd-modale" role="dialog" aria-labelledby="fd-num" onClick={e => e.stopPropagation()}>
        <div className="modal-header fd-entete">
          <div className="fd-entete-gauche">
            <div className="fd-eyebrow">Facture atelier{entete ? ` · ${entete}` : ''}</div>
            <div className="fd-num" id="fd-num">
              {facture.num_facture || 'Facture'}
              {facture.num_facture && (
                <button type="button" className="fd-copier" onClick={copierNumero}>{copie ? 'Copié' : 'Copier'}</button>
              )}
            </div>
            <div className="fd-meta">
              {facture.num_or && (
                <span>OR {onOuvrirOr
                  ? <button type="button" className="fd-or-lien" onClick={() => onOuvrirOr(facture.num_or)}>{facture.num_or}</button>
                  : <b>{facture.num_or}</b>}
                </span>
              )}
              {facture.receptionnaire && <span>Réceptionnaire <b>{facture.receptionnaire}</b></span>}
            </div>
          </div>
          <div className="fd-total">
            <div className={`fd-montant${total < 0 ? ' fd-negatif' : ''}`}>{montantFr(total)}<small>HT</small></div>
            <div className="fd-date">Facturée le {dateFr(dFacture)}</div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Fermer">✕</button>
        </div>

        <div className="modal-body fd-corps">
          {dOuverture && dFacture && (
            <div className="fd-parcours" aria-label="Parcours de l'OR">
              <div className="fd-jalon"><span>OR ouvert</span><b>{dateFr(dOuverture)}</b></div>
              <div className="fd-trait">
                {duree != null && duree >= 0 && <span className="fd-duree">{duree === 0 ? 'le jour même' : `${duree} jour${duree > 1 ? 's' : ''}`}</span>}
              </div>
              <div className="fd-jalon fd-jalon-fin"><span>Facturé</span><b>{dateFr(dFacture)}</b></div>
            </div>
          )}

          <div className="fd-blocs">
            <div className="fd-bloc">
              <h3>Véhicule</h3>
              <dl className="fd-kv">
                <dt>Immatriculation</dt><dd>{facture.immat ? <span className="plaque-badge">{facture.immat}</span> : '—'}</dd>
                <dt>Marque</dt><dd>{facture.marque || '—'}</dd>
                <dt>VIN</dt><dd className="fd-vin">{facture.vin || '—'}</dd>
              </dl>
            </div>
            <div className="fd-bloc">
              <h3>Client</h3>
              <dl className="fd-kv">
                <dt>Facturé à</dt><dd>{facture.nom_client || '—'}</dd>
                <dt>Société</dt><dd>{facture.societe || '—'}</dd>
                <dt>Département</dt><dd>{facture.department || '—'}</dd>
              </dl>
            </div>
          </div>

          {groupes.length > 1 && sommeAbs > 0 && (
            <div className="fd-repart" aria-label="Répartition du montant">
              <div className="fd-barre" aria-hidden="true">
                {groupes.map(g => (
                  <span key={g.cle} className={`fd-barre-${g.cle}`} style={{ width: `${(Math.abs(g.total) / sommeAbs) * 100}%` }} />
                ))}
              </div>
              <div className="fd-legende">
                {groupes.map(g => (
                  <span key={g.cle}><i className={`fd-barre-${g.cle}`} />{g.titre} <b>{montantFr(g.total)}</b></span>
                ))}
              </div>
            </div>
          )}

          {nbLignes === 0 ? (
            <p className="fd-vide">Aucune ligne de détail pour cette facture.</p>
          ) : (
            <div className="fd-lignes">
              <table className="fd-table">
                <thead>
                  <tr>
                    <th>Désignation</th>
                    <th>Réf.</th>
                    <th className="fd-nb">Qté</th>
                    <th className="fd-nb">Montant HT</th>
                  </tr>
                </thead>
                <tbody>
                  {groupes.map(g => (
                    <React.Fragment key={g.cle}>
                      <tr className={`fd-groupe fd-groupe-${g.cle}`}>
                        <td colSpan={3}><span className="fd-pastille">{g.titre} · {g.lignes.length} ligne{g.lignes.length > 1 ? 's' : ''}</span></td>
                        <td className="fd-nb">{montantFr(g.total)}</td>
                      </tr>
                      {g.lignes.map((l, i) => {
                        const operation = g.cle === 'mo' && l.prestation && l.prestation !== l.designation
                          && !/^m\.[o0]\.$/i.test(l.prestation) ? l.prestation : null;
                        return (
                          <tr key={`${g.cle}-${i}`}>
                            <td>
                              {l.designation || l.prestation || '—'}
                              {operation && <div className="fd-operation">{operation}</div>}
                            </td>
                            <td className={l.ref ? 'fd-ref' : 'fd-muet'}>{l.ref || '—'}</td>
                            <td className="fd-nb">{l.qte != null && l.qte !== '' ? Number(l.qte).toLocaleString('fr-FR') : '—'}</td>
                            <td className={`fd-nb${Number(l.montant_ht) < 0 ? ' fd-negatif' : ''}`}>{montantFr(l.montant_ht)}</td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>Total HT · {nbLignes} ligne{nbLignes > 1 ? 's' : ''}</td>
                    <td className={`fd-nb${total < 0 ? ' fd-negatif' : ''}`}>{montantFr(total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        <div className="modal-footer fd-pied">
          <button className="btn-primary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

export default FactureDetailModal;
